import type { CreatureState } from "@eldrun/content";

/**
 * Quais criaturas usam o modelo animado (F50): as mais perto, até um teto (12 no Médio e no Alto, 6 no
 * Baixo), com histerese para o corpo não trocar a cada passo. Puro: a camada chama `tick` a cada
 * `ANIMATED_SET.tickMs` com as candidatas do quadro e aplica as trocas que ele devolve.
 */
export const ANIMATED_SET = {
  /** Entra com posição ≤ teto e a até `promote` m × distância de visão; sai acima de teto + 2 ou além de `demote` m. */
  promote: 45,
  demote: 52,
  /**
   * As que animam até onde são desenhadas (`AnimatedCandidate.far`: o Dragão): entram até a distância de
   * desenho das criaturas (`CREATURE_DRAW_RADIUS`) e saem 7 m depois (a mesma folga de 45 para 52).
   */
  far: 140,
  /** Folga de posições antes de sair (entra até 12, sai depois de 14). */
  slack: 2,
  /**
   * Com o conjunto cheio, distância a mais (m, ou a fração `marginShare` da distância, o que for maior)
   * para uma nova tomar a vaga de uma da folga: numa roda de criaturas quase à mesma distância, ninguém
   * fica trocando.
   */
  margin: 4,
  marginShare: 0.2,
  /** No máximo tantas entradas por rodada, e cada criatura troca no máximo uma vez a cada `swapMs`. */
  promotionsPerTick: 2,
  swapMs: 1000,
  tickMs: 250,
} as const;

/** A distância a mais que conta como "bem mais perto" de uma criatura a `distance` m. */
export const swapMargin = (distance: number): number => Math.max(ANIMATED_SET.margin, distance * ANIMATED_SET.marginShare);

/** Teto de criaturas animadas do preset (Opções → Gráficos): 6 no Baixo, 12 no Médio e no Alto. */
export const animatedLimit = (preset: string): number => (preset === "baixo" ? 6 : 12);

/** Estados em que o corpo não troca: o golpe, a morte e as dissoluções ficariam pela metade. */
export const LOCKED_STATES: ReadonlySet<CreatureState> = new Set(["preparando", "golpeando", "morta", "dissolvendo", "sumindo"]);

export interface AnimatedCandidate {
  readonly id: string;
  /** Distância da câmera (m). */
  readonly distance: number;
  /** Chefes, Guardiões e o Dragão primeiro (1), as outras depois (0). */
  readonly priority: number;
  readonly state: CreatureState;
  /** O tipo tem modelo animado carregado (sem ele, a criatura fica procedural). */
  readonly ready: boolean;
  /** Anima até a distância de desenho (`ANIMATED_SET.far`), não só até 45/52 m: o Dragão. */
  readonly far?: boolean;
}

/** Até onde a candidata entra e de onde ela sai (m, antes da distância de visão). */
const promoteAt = (c: AnimatedCandidate) => (c.far ? ANIMATED_SET.far : ANIMATED_SET.promote);
const demoteAt = (c: AnimatedCandidate) => (c.far ? ANIMATED_SET.far + ANIMATED_SET.demote - ANIMATED_SET.promote : ANIMATED_SET.demote);

export interface AnimatedChanges {
  readonly promote: string[];
  readonly demote: string[];
}

/**
 * O conjunto animado. Ordena as prontas por prioridade e distância; entra quem está até o teto e dentro
 * do alcance; sai quem passa de teto + folga ou do alcance de saída (ou deixou de estar pronta), nunca
 * no meio de um golpe, morte ou dissolução (`LOCKED_STATES`). O conjunto nunca passa do teto: com ele
 * cheio, uma criatura bem colocada toma a vaga da pior que está na folga, se estiver bem mais perto
 * (`swapMargin`) ou for chefe: duas quase à mesma distância não ficam trocando. Se o teto baixar, as
 * piores saem na hora. Quem sai do schema sai na hora (`forget`).
 */
export class AnimatedSet {
  private readonly members = new Set<string>();
  private readonly lastSwap = new Map<string, number>();
  /** Trocas feitas (entradas e saídas), para o `stats()`. */
  swaps = 0;

  has(id: string): boolean {
    return this.members.has(id);
  }

  get size(): number {
    return this.members.size;
  }

  /** A criatura saiu do schema (morreu de vez, sumiu): sai sem troca. */
  forget(id: string): void {
    this.members.delete(id);
    this.lastSwap.delete(id);
  }

  /** A entrada desta rodada não aconteceu (sem vaga para o corpo agora): tenta de novo na próxima, sem contar troca. */
  refuse(id: string): void {
    if (!this.members.delete(id)) return;
    this.lastSwap.delete(id);
    this.swaps--;
  }

  /** Uma rodada: `cap` é o teto e `range` a distância de visão da qualidade (multiplica os alcances). */
  tick(now: number, candidates: readonly AnimatedCandidate[], cap: number, range: number): AnimatedChanges {
    // Só contam as prontas a até o alcance de saída: as de longe nunca ficam animadas e não tomam posição.
    const ranked = candidates.filter((c) => c.ready && c.distance <= demoteAt(c) * range).sort((a, b) => b.priority - a.priority || a.distance - b.distance || (a.id < b.id ? -1 : 1));
    const rank = new Map(ranked.map((c, i) => [c.id, i + 1]));
    const byId = new Map(candidates.map((c) => [c.id, c]));
    const canSwap = (c: AnimatedCandidate) => !LOCKED_STATES.has(c.state) && now - (this.lastSwap.get(c.id) ?? -Infinity) >= ANIMATED_SET.swapMs;
    const demote: string[] = [];
    const promote: string[] = [];
    const leave = (id: string) => {
      this.members.delete(id);
      this.lastSwap.set(id, now);
      demote.push(id);
      this.swaps++;
    };
    for (const id of [...this.members]) {
      const c = byId.get(id);
      if (!c) {
        this.forget(id);
        continue;
      }
      const r = rank.get(id);
      const out = r === undefined || r > cap + ANIMATED_SET.slack || c.distance > demoteAt(c) * range;
      if (out && canSwap(c)) leave(id);
    }
    // O teto baixou (o Automático foi para o Baixo, ou o jogador trocou o preset): saem as piores na
    // hora, sem esperar o intervalo entre trocas nem a folga; só o golpe, a morte e as dissoluções seguram.
    if (this.members.size > cap) {
      const excess = [...this.members]
        .map((id) => ({ id, r: rank.get(id) ?? Infinity, c: byId.get(id)! }))
        .filter((m) => !LOCKED_STATES.has(m.c.state))
        .sort((a, b) => b.r - a.r || (a.id < b.id ? -1 : 1));
      for (const m of excess.slice(0, this.members.size - cap)) leave(m.id);
    }
    for (const c of ranked) {
      if (promote.length >= ANIMATED_SET.promotionsPerTick) break;
      const r = rank.get(c.id)!;
      if (r > cap) break;
      if (this.members.has(c.id) || c.distance > promoteAt(c) * range || !canSwap(c)) continue;
      if (this.members.size >= cap) {
        // Cheio: a pior animada na folga (posição > teto) cede a vaga, se puder trocar agora.
        const worst = [...this.members]
          .map((id) => ({ id, r: rank.get(id) ?? Infinity, c: byId.get(id)! }))
          .filter((m) => m.r > cap && canSwap(m.c) && (c.priority > m.c.priority || m.c.distance - c.distance >= swapMargin(m.c.distance)))
          .sort((a, b) => b.r - a.r)[0];
        if (!worst) continue;
        leave(worst.id);
      }
      this.members.add(c.id);
      this.lastSwap.set(c.id, now);
      promote.push(c.id);
      this.swaps++;
    }
    return { promote, demote };
  }
}
